import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { MdDeleteForever } from "react-icons/md";
import { useGetMyOutletFoodItemsQuery } from "../slices/FoodItemSilce";
import { Button, Col, Form, Row, Table } from "react-bootstrap";
import ReUsableModal from "../components/ReUsableModal";
import { toast } from "react-toastify";
import { useCreateOuletOrderMutation } from "../slices/OrdersSlice";
import LazyMemberImage from "../components/LazyMemberImage";

const SalesPage = () => {
  const { userInfo } = useSelector((state) => state.auth);
  const [itemCode, setItemCode] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [itemName, setItemName] = useState("");
  const [sellingPrice, setSellingPrice] = useState("");
  const [gst, setGst] = useState("");
  const [totalPrice, setTotalPrice] = useState("");
  const [orderItems, setOrderItems] = useState([]);
  const [matchingItems, setMatchingItems] = useState([]);
  const [selectedItemId, setSelectedItemId] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [grossTotal, setGrossTotal] = useState("0.00");
  const [availableQty, setAvailableQty] = useState(null);

  const {
    data: foodItems,
    isLoading,
    refetch,
    error,
  } = useGetMyOutletFoodItemsQuery({ token: userInfo.token });

  const [placeOrder, { isLoading: placeOrderLoading }] =
    useCreateOuletOrderMutation();

  useEffect(() => {
    if (!itemCode || !foodItems) {
      setMatchingItems([]);
      return;
    }

    const matches = foodItems.filter((item) => item.itemCode === itemCode);
    setMatchingItems(matches);

    if (matches.length === 1) {
      const selected = matches[0];
      fillItemDetails(selected);
    } else {
      setItemName("");
      setSellingPrice("");
      setTotalPrice("");
    }
  }, [itemCode, foodItems]);

  useEffect(() => {
    if (selectedItemId && matchingItems.length > 1) {
      const selected = matchingItems.find(
        (item) => item._id === selectedItemId
      );
      if (selected) {
        fillItemDetails(selected);
      }
    }
  }, [selectedItemId, quantity]);

  useEffect(() => {
    if (quantity && matchingItems.length === 1) {
      fillItemDetails(matchingItems[0]);
    }
  }, [quantity]);

  const fillItemDetails = (item) => {
    const offer = item.itemOfferPrice || 0;
    const discountPercent = item.discountPercent || 0;
    const discountAmount = item.discountAmount || 0;

    const priceAfterPercent = offer - (offer * discountPercent) / 100;
    const finalSellingPrice = priceAfterPercent - discountAmount;

    setItemName(item.itemName);
    setSellingPrice(finalSellingPrice.toFixed(2));

    if (quantity) {
      if (Number(quantity) > item.itemQuantity) {
        // toast.error(`Only ${item.itemQuantity} is available.`);
        setTotalPrice("");
        setAvailableQty(item.itemQuantity);
      } else {
        const total = finalSellingPrice * Number(quantity);
        setTotalPrice(total.toFixed(2));
        setAvailableQty(10000);
      }
    }
  };

  const handleKeyDown = (e) => {
    if (
      e.key === "Enter" &&
      itemCode &&
      quantity &&
      itemName &&
      sellingPrice &&
      totalPrice
    ) {
      const selectedItem = foodItems.find(
        (item) =>
          item.itemCode === itemCode &&
          item.itemName === itemName &&
          (matchingItems.length === 1 || item._id === selectedItemId)
      );
      if (!selectedItem) {
        toast.error("Invalid Item Code.");
        return;
      }

      const isDuplicate = orderItems.some(
        (item) =>
          item.itemCode === selectedItem.itemCode &&
          item.itemName === selectedItem.itemName
      );
      if (isDuplicate) {
        toast.error("Item already added.");
        return;
      }

      const gstPercent = userInfo?.outletId.filter(
        (ot) => ot._id === userInfo?.selectedOutletId
      )[0].separateGst
        ? selectedItem?.gstTax
        : 0;

      const gstAmountPerUnit = (parseFloat(sellingPrice) * gstPercent) / 100;
      const totalGstAmount = gstAmountPerUnit * Number(quantity);

      const baseTotalPrice = parseFloat(sellingPrice) * Number(quantity);
      const totalPriceWithGst = baseTotalPrice + totalGstAmount;

      const newItem = {
        _id: selectedItem._id,
        imageUploadedTime: selectedItem.imageUploadedTime,
        currentlySelling: selectedItem.currentlySelling,
        imageUploaded: selectedItem.imageUploaded,
        itemCode,
        itemName,
        quantity,
        sellingPrice,
        totalPrice: totalPriceWithGst.toFixed(2),
        gstAmount: totalGstAmount.toFixed(2),
        gstPercent: gstPercent,
      };

      if (Number(quantity) > selectedItem.itemQuantity) {
        toast.error(`Only ${selectedItem.itemQuantity} is available.`);
        return; // Prevent adding
      }

      setOrderItems([...orderItems, newItem]);

      // Reset fields
      setItemCode("");
      setQuantity(1);
      setItemName("");
      setSellingPrice("");
      setTotalPrice("");
      setSelectedItemId("");
    }
  };

  const generateOrderDetailsAndGrossTotal = (
    orderItems,
    foodItems,
    userInfo
  ) => {
    const orderDetails = orderItems.map((orderItem) => {
      const foodItem = foodItems.find(
        (item) => item.itemCode === orderItem.itemCode
      );

      const quantity = orderItem.quantity;
      const offerPrice = foodItem?.itemOfferPrice || 0;
      const discountPercent = foodItem?.discountPercent || 0;
      const discountAmount = foodItem?.discountAmount || 0;
      const gstPercent = userInfo?.outletId.filter(
        (ot) => ot._id === userInfo?.selectedOutletId
      )[0].separateGst
        ? foodItem?.gstTax
        : 0;

      const discountFromPercent = (offerPrice * discountPercent) / 100;
      const sellingPrice = offerPrice - discountFromPercent - discountAmount;
      const gstAmount = (sellingPrice * gstPercent) / 100;

      return {
        foodItemId: foodItem?._id,
        itemQuantity: quantity,
        gstAmount,
        sellingPrice,
      };
    });

    const baseTotal = orderDetails.reduce(
      (total, it) => total + (it.sellingPrice + it.gstAmount) * it.itemQuantity,
      0
    );

    const grossTotal = Number(baseTotal.toFixed(2));

    return { orderDetails, grossTotal };
  };

  const totalOrderAmount = orderItems
    .reduce((sum, item) => sum + parseFloat(item.totalPrice), 0)
    .toFixed(2);

  const handleConfirmOrder = () => {
    const { grossTotal } = generateOrderDetailsAndGrossTotal(
      orderItems,
      foodItems,
      userInfo
    );
    setGrossTotal(grossTotal);
    setShowModal(true);
  };

  const handleSubmit = async () => {
    try {
      const { orderDetails, grossTotal } = generateOrderDetailsAndGrossTotal(
        orderItems,
        foodItems,
        userInfo
      );

      const orderPayload = {
        outletId: foodItems[0]?.outletId?._id,
        orderDetails,
        grossTotal,
        token: userInfo?.token,
      };

      const orderResponse = await placeOrder(orderPayload).unwrap();

      if (orderResponse) {
        toast.success("Order Placed Successfully");
        setOrderItems([]);
        setShowModal(false);
        refetch();
      } else {
        toast.error("Order Placement Failed");
      }
    } catch (err) {
      console.error(err);
      toast.error(
        err?.data?.message || "Something went wrong while placing the order."
      );
    }
  };
  const handleCancel = () => {
    setShowModal(false);
  };

  const handleDeleteItem = (indexToDelete) => {
    const updatedItems = orderItems.filter(
      (_, index) => index !== indexToDelete
    );
    setOrderItems(updatedItems);
  };

  return (
    <>
      <h6>Sales Page</h6>
      <ReUsableModal
        title="Are you Sure to ProcessOrder"
        size="md"
        payload={[
          {
            fieldName: "Item & Quantity",
            fieldType: "text",
            fieldValue: orderItems
              .map((item) => `${item.itemName} - ${item.quantity}`)
              .join(", "),
            disabled: true,
          },
          {
            fieldName: "Gross Total",
            fieldType: "text",
            fieldValue: `₹${grossTotal}`,
            disabled: true,
          },
        ]}
        show={showModal}
        onSubmit={handleSubmit}
        onHide={handleCancel}
        submit="Submit"
        cancel="Cancel"
      />
      <Form onKeyDown={handleKeyDown}>
        <Row className="mb-3">
          <Col>
            <Form.Control
              style={{ fontSize: "1.3rem" }}
              type="text"
              value={itemCode}
              onChange={(e) => {
                setItemCode(e.target.value);
                setItemName("");
                setSellingPrice("");
                setTotalPrice("");
              }}
              placeholder=" Item Code"
            />
          </Col>
          <Col>
            <Form.Control
              style={{ fontSize: "1.3rem" }}
              type="number"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              placeholder=" Quantity"
            />
          </Col>
          <Col>
            {matchingItems.length > 1 ? (
              <Form.Select
                style={{ fontSize: "1.3rem" }}
                value={selectedItemId}
                onChange={(e) => setSelectedItemId(e.target.value)}
              >
                <option value="">Select Item</option>
                {matchingItems.map((item) => (
                  <option key={item._id} value={item._id}>
                    {item.itemName}
                  </option>
                ))}
              </Form.Select>
            ) : (
              <Form.Control
                style={{ fontSize: "1.3rem" }}
                type="text"
                value={itemName}
                placeholder="Item Name"
                disabled
              />
            )}
          </Col>
          <Col>
            <Form.Control
              style={{ fontSize: "1.3rem" }}
              type="text"
              value={sellingPrice}
              placeholder="Selling Price"
              disabled
            />
          </Col>
          <Col>
            <Form.Control
              style={{ fontSize: "1.3rem" }}
              type="text"
              value={totalPrice}
              placeholder="Total"
              disabled
            />
          </Col>
          {availableQty !== 10000 && availableQty !== null && (
            <label style={{ color: "red" }}>
              {" "}
              Available Quantity : {availableQty}
            </label>
          )}
        </Row>
      </Form>

      <hr />

      {/* Render added items */}
      {orderItems?.length > 0 && (
        <div
          style={{
            maxHeight: "60vh",
            overflowY: "auto",
          }}
        >
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th></th>
                <th>#</th>
                <th>Item Name</th>
                <th>Selling Price</th>
                <th>Quantity</th>
                <th>GST</th>
                <th>Total Price</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {orderItems.map((item, index) => (
                <tr key={index}>
                  <td>
                    <LazyMemberImage
                      foodItemId={item._id}
                      imageUpdatedTimeFromDB={item.imageUploadedTime}
                      currentlySelling={item.currentlySelling}
                      photoUploaded={item.imageUploaded}
                    />
                  </td>
                  <td>{index + 1}</td>
                  <td>{item?.itemName}</td>
                  <td>₹{item?.sellingPrice}</td>
                  <td>{item?.quantity}</td>
                  <td>
                    {" "}
                    <div>{item?.gstAmount}</div>
                    <div>{item?.gstPercent}%</div>
                  </td>
                  <td>₹{item?.totalPrice}</td>
                  <td>
                    <Button onClick={() => handleDeleteItem(index)}>
                      <MdDeleteForever />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
      {/* Order Total and Confirm Button */}
      <Row className="mt-4 justify-content-center">
        <Col xs="auto">
          <Form.Label>
            <strong> Total: ₹{totalOrderAmount}</strong>
          </Form.Label>
        </Col>
      </Row>

      <Row className="mt-2 justify-content-center">
        <Col xs="auto">
          <Button variant="primary" onClick={handleConfirmOrder}>
            Confirm Order
          </Button>
        </Col>
      </Row>
    </>
  );
};

export default SalesPage;
